// Chakra imports
import {
  Flex,
  Text,
  useColorModeValue,
  Stack,
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  Input,
  useToast,
} from "@chakra-ui/react";
// Custom components
import IconBox from "./../../../../components/Icons/IconBox";
import { DocumentIcon } from "./../../../../components/Icons/Icons";
import Card from "./../../../../components/Card/Card";
import CardBody from "./../../../../components/Card/CardBody";
import CardHeader from "./../../../../components/Card/CardHeader";
import React, { useRef } from "react";
import { gql, useMutation } from "@apollo/client";

const uploadFileGql = gql`
  mutation uploadPlannerItemMedia($input: UploadPlannerItemMediaInput!) {
    uploadPlannerItemMedia(input: $input)
  }
`;

const deleteFileGql = gql`
  mutation deletePlannerItemMedia($input: DeletePlannerItemMediaInput!) {
    deletePlannerItemMedia(input: $input)
  }
`;

const updateMediaGql = gql`
  mutation updateMedia($input: updateMediaInput!) {
    updateMedia(input: $input) {
      id
    }
  }
`;

const MovingPlannerItemMediaUpload = ({ data, forceRerender }) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();
  const [uploadFile, { loading: loadingUpload, data: dataUpload }] = useMutation(uploadFileGql);
  const [deleteFile, { loading: loadingDelete, data: dataDelete }] = useMutation(deleteFileGql);
  const [updateMedia] = useMutation(updateMediaGql);

  const inputRef = useRef(null);
  const textColor = useColorModeValue("gray.700", "white");
  const bgButton = useColorModeValue("linear-gradient(81.62deg, #313860 2.25%, #151928 79.87%)", "gray.800");
  const iconBoxInside = useColorModeValue("white", "white");

  if (!loadingUpload && dataUpload?.uploadAffiliatePartnerDoc) {
    setTimeout(() => {
      forceRerender();
    }, 500);
  }

  if (!loadingDelete && dataDelete) {
    setTimeout(() => {
      forceRerender();
    }, 500);
  }

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const updateData = async (id, field, data) => {
    let inputData = JSON.parse('{"' + field + '":"' + data + '"}');
    inputData.id = id;
    await updateMedia({ variables: { input: inputData } });

    toast({
      title: "Ændringen er blevet gemt.",
      description: "",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleDeleteClick = () => {
    onOpen();
  };

  const deleteMedia = () => {
    onClose();

    const inputData = {
      plannerItemId: data.id,
    };

    deleteFile({ variables: { input: inputData } });
  };

  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  async function handleFileChange(e) {
    if (!e.target.files) {
      return;
    }

    const fileBase64 = await toBase64(e.target.files[0]);

    const inputData = {
      plannerItemId: data.id,
      filename: e.target.files[0].name,
      mimeType: e.target.files[0].type,
      fileContentBase64: fileBase64,
    };

    await uploadFile({ variables: { input: inputData } });
  }

  const downloadMedia = (docUrl) => {
    window.open(docUrl);
  };

  let showFile = false;

  if (data.media) {
    showFile = true;
  }
  return (
    <Card p="16px" my={{ sm: "24px", xl: "24px" }}>
      <CardHeader style={{ justifyContent: "space-between" }}>
        <Text fontSize="lg" color={textColor} fontWeight="bold">
          Media
        </Text>

        {!showFile ? (
          <Text fontSize="lg" color="gray.400" cursor="pointer" onClick={() => handleUploadClick()}>
            Upload
          </Text>
        ) : (
          <Text fontSize="lg" color="gray.400" cursor="pointer" onClick={() => handleDeleteClick()}>
            Slet
          </Text>
        )}
        <input type="file" ref={inputRef} onChange={handleFileChange} style={{ display: "none" }} />
      </CardHeader>

      <CardBody w="100%" pt="28px" style={{ flexDirection: "column" }}>
        {showFile && (
          <Stack direction="column" spacing="24px" w="100%" style={{ paddingTop: "10px" }}>
            <Flex align="center" w="100%">
              <Flex align="center">
                <IconBox as="box" h={"40px"} w={"40px"} bg={bgButton} me="18px">
                  <DocumentIcon h={"20px"} w={"20px"} color={iconBoxInside} />
                </IconBox>
                <Flex direction="column" cursor="pointer" onClick={() => downloadMedia(data.media.mediaUrl)}>
                  <Text fontSize="sm" fontWeight="bold" color={textColor}>
                    {data.media.filename}
                  </Text>
                  <Text color="gray.400" fontSize="xs">
                    {data.media.filename}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
            <Flex align="center" mb="18px">
              <Text fontSize="md" color={textColor} fontWeight="bold" me="10px" whiteSpace={"nowrap"} minWidth={"160px"}>
                Overskrift:{" "}
              </Text>
              <Input
                variant="flushed"
                type="text"
                name="heading"
                defaultValue={data.media.heading}
                onBlur={(e) => updateData(data.media.id, "heading", e.target.value)}
              />
            </Flex>
            <Flex align="center" mb="18px">
              <Text fontSize="md" color={textColor} fontWeight="bold" me="10px" whiteSpace={"nowrap"} minWidth={"160px"}>
                Text:{" "}
              </Text>
              <Input
                variant="flushed"
                type="text"
                name="text"
                defaultValue={data.media.text}
                onBlur={(e) => updateData(data.media.id, "text", e.target.value)}
              />
            </Flex>
          </Stack>
        )}
      </CardBody>
      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={deleteMedia}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Slet media
            </AlertDialogHeader>

            <AlertDialogBody>Er du sikker?</AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Fortryd
              </Button>
              <Button colorScheme="red" onClick={deleteMedia} ml={3}>
                Slet
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Card>
  );
};

export default MovingPlannerItemMediaUpload;
